import { useState, useEffect } from 'react';

// 表示したいブランドのリスト
const BRANDS = [
  { name: 'ロレックス', slug: 'rolex' },
  { name: 'カルティエ', slug: 'cartier' },
  { name: 'IWC', slug: 'iwc' },
  { name: 'チューダー', slug: 'tudor' },
  { name: 'ショパール', slug: 'chopard' },
  { name: 'テンデンス', slug: 'tendence' },
];

const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1523275335684-37898b6baf30';

// 記事の型（エラー防止のための定義）
interface WPPost {
  id: number;
  title: string;
  image: string;
  date: string;
  link: string;
}

// ブランドごとの枠のデザイン
function BrandSection({ brandName, posts, loading, domain }: { brandName: string, posts: WPPost[], loading: boolean, domain: string }) {
  const displayPosts = posts.slice(0, 4);

  return (
    <div className="border border-gray-100 rounded-lg p-6 bg-white shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 h-px bg-gray-200" />
        <h3 className="text-base font-bold text-gray-800 whitespace-nowrap tracking-wider">{brandName}</h3>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : displayPosts.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-10">記事はまだありません</p>
      ) : (
        <div className="space-y-4 text-left">
          {displayPosts.map((post) => (
            <a key={post.id} href={post.link} className="flex gap-3 group cursor-pointer">
              <div className="w-24 h-16 flex-shrink-0 rounded overflow-hidden bg-gray-50">
                <img src={post.image} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" alt="" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900 group-hover:text-amber-600 transition-colors line-clamp-2 leading-snug" dangerouslySetInnerHTML={{ __html: post.title }} />
                <p className="text-[10px] text-gray-400 mt-1">{post.date}</p>
              </div>
            </a>
          ))}
        </div>
      )}

      {!loading && (
        <div className="mt-6 text-center">
          <a href={`${domain}/category/${encodeURIComponent(brandName)}`} className="inline-flex items-center gap-1 px-4 py-1.5 border border-gray-200 text-gray-500 text-xs rounded-full hover:border-amber-500 hover:text-amber-600 transition-colors">
            もっと見る <i className="ri-arrow-right-line text-xs" />
          </a>
        </div>
      )}
    </div>
  );
}

export default function Services() {
  const [postsByBrand, setPostsByBrand] = useState<Record<string, WPPost[]>>({});
  const [loading, setLoading] = useState(true);
  
  // 【重要】ここをご自身のWordPressドメイン（例: https://chronocharm.xyz）に書き換えてください
  const domain = 'https://chronocharm.xyz';

  useEffect(() => {
    const fetchAllBrandPosts = async () => {
      const grouped: Record<string, WPPost[]> = {};
      
      for (const brand of BRANDS) {
        try {
          const wpUrl = `${domain}/wp-json/wp/v2/posts?_embed&per_page=4&search=${encodeURIComponent(brand.name)}`;
          const res = await fetch(wpUrl);
          const data = await res.json();
          
          grouped[brand.name] = data.map((post: any) => ({
            id: post.id,
            title: post.title.rendered,
            image: post._embedded?.['wp:featuredmedia']?.[0]?.source_url || DEFAULT_IMAGE,
            date: new Date(post.date).toLocaleDateString('ja-JP'),
            link: post.link,
          }));
        } catch (e) {
          grouped[brand.name] = [];
        }
      }
      setPostsByBrand(grouped);
      setLoading(false);
    };

    fetchAllBrandPosts();
  }, [domain]);

  return (
    <section id="services" className="py-24 bg-[#fcfcfc]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">取扱ブランド</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            ロレックスやチューダーなど、各ブランドの最新記事をお届けします。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {BRANDS.map(({ name }) => (
            <BrandSection key={name} brandName={name} posts={postsByBrand[name] || []} loading={loading} domain={domain} />
          ))}
        </div>
      </div>
    </section>
  );
}